import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Lock, LogOut, Building2, GraduationCap, CalendarDays, Inbox, Home as HomeIcon,
  Plus, Trash2, Edit3, X, Check, Upload, ImageIcon, ChevronLeft, ChevronRight, Reply, MailCheck, BedDouble, Eye, EyeOff, Search, Calculator,
} from "lucide-react";
import { PROPERTY_STATUSES, PROPERTY_TYPES, statusMeta, formatPrice } from "../mock/mock";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import EstimatorAdmin from "../components/EstimatorAdmin";
import {
  login as apiLogin, logout as apiLogout, getMe, mediaUrl, uploadImage,
  listProperties, listAllProperties, createProperty, updateProperty, updatePropertyStatus, deleteProperty, listPropertyCategories, catalogStatus, catalogImport,
  listFormations, createFormation, updateFormation, deleteFormation,
  listEvents, createEvent, updateEvent, deleteEvent, listLeads, sendEmailTest, listAdminRentals, setRentalVisibility,
  listServices, createService, updateService, deleteService,
} from "../lib/api";

const SERVICE_CATEGORIES = [
  { value: "short_term", label: "Location courte durée" },
  { value: "long_term", label: "Location longue durée" },
  { value: "management", label: "Gestion immobilière" },
];
const catLabel = (v) => (SERVICE_CATEGORIES.find((c) => c.value === v) || {}).label || v;

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [tab, setTab] = useState("properties");

  const [properties, setProperties] = useState([]);
  const [formations, setFormations] = useState([]);
  const [events, setEvents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [rentals, setRentals] = useState({ enabled: false, items: [] });

  const reloadAll = useCallback(() => {
    listAllProperties().then(setProperties).catch(() => {});
    listFormations().then(setFormations).catch(() => {});
    listEvents().then(setEvents).catch(() => {});
    listLeads().then(setLeads).catch(() => {});
    listServices().then(setServices).catch(() => {});
    listAdminRentals().then(setRentals).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.removeItem("bella_token");
    getMe().then(() => setAuthed(true)).catch(() => setAuthed(false)).finally(() => setChecking(false));
  }, []);

  useEffect(() => { if (authed) reloadAll(); }, [authed, reloadAll]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await apiLogin(creds.username, creds.password);
      setAuthed(true);
      toast({ title: "Connexion réussie", description: "Bienvenue dans l'espace admin." });
    } catch (err) {
      const status = err?.response?.status;
      const detail = typeof err?.response?.data?.detail === "string" ? err.response.data.detail : null;
      toast({ title: status === 429 ? "Trop de tentatives" : "Échec de connexion", description: detail || "Identifiants incorrects." });
    }
  };
  const logout = async () => { try { await apiLogout(); } catch { /* cookie already gone */ } setAuthed(false); };

  if (checking) {
    return <div className="min-h-screen bg-[#0b1f33] flex items-center justify-center text-white/70">Chargement…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0b1f33] flex items-center justify-center px-6">
        <Toaster />
        <div className="w-full max-w-md bg-white rounded-lg p-10 shadow-2xl">
          <div className="w-14 h-14 rounded-full bg-[#0b1f33] flex items-center justify-center mx-auto">
            <Lock size={26} className="text-[#2f74c9]" />
          </div>
          <img src="/brand/bella-logo.png" alt="Bella Edifices" className="h-12 w-auto object-contain mx-auto mt-5" />
          <h1 className="font-serif-display text-2xl text-[#0b1f33] text-center mt-3">Espace administrateur</h1>
          <p className="text-slate-400 text-sm text-center mt-2">Bella Edifices — gestion du contenu</p>
          <form onSubmit={login} className="mt-8 space-y-4">
            <input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} placeholder="Identifiant" className="w-full rounded-md border border-slate-200 px-4 py-3 outline-none focus:border-[#2f74c9] focus:ring-2 focus:ring-[#2f74c9]/20" />
            <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="Mot de passe" className="w-full rounded-md border border-slate-200 px-4 py-3 outline-none focus:border-[#2f74c9] focus:ring-2 focus:ring-[#2f74c9]/20" />
            <button className="btn-gold w-full py-3.5 rounded-sm font-600">Se connecter</button>
          </form>
          <Link to="/" className="block text-center text-sm text-[#2f74c9] mt-6 hover:underline">Retour au site</Link>
        </div>
      </div>
    );
  }

  const TABS = [
    { key: "properties", label: "Propriétés", Icon: Building2, count: properties.length },
    { key: "services", label: "Services", Icon: HomeIcon, count: services.length },
    { key: "rentals", label: "Locations Guesty", Icon: BedDouble, count: rentals.items.length },
    { key: "estimator", label: "Simulateur", Icon: Calculator },
    { key: "formations", label: "Formations", Icon: GraduationCap, count: formations.length },
    { key: "events", label: "Événements", Icon: CalendarDays, count: events.length },
    { key: "leads", label: "Messages", Icon: Inbox, count: leads.length },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <div className="bg-[#0b1f33] text-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <img src="/brand/bella-logo-white.png" alt="Bella Edifices" className="h-8 w-auto object-contain inline-block align-middle" />
            <span className="ml-3 text-xs uppercase tracking-widest text-white/50 align-middle">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/70 hover:text-white">Voir le site</Link>
            <button onClick={logout} className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-md transition-colors">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-3 mb-8">
          {TABS.map(({ key, label, Icon, count }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`} className={`flex items-center gap-2 px-5 py-3 rounded-lg font-600 text-sm transition-colors ${tab === key ? "bg-[#0b1f33] text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-[#2f74c9]"}`}>
              <Icon size={18} className={tab === key ? "text-[#2f74c9]" : ""} /> {label}
              <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${tab === key ? "bg-[#2f74c9] text-white" : "bg-slate-100"}`}>{count}</span>
            </button>
          ))}
        </div>

        {tab === "properties" && <PropertiesAdmin items={properties} reload={() => listAllProperties().then(setProperties)} />}
        {tab === "services" && <ServicesAdmin items={services} reload={() => listServices().then(setServices)} />}
        {tab === "rentals" && <RentalsAdmin data={rentals} reload={() => listAdminRentals().then(setRentals)} />}
        {tab === "estimator" && <EstimatorAdmin />}
        {tab === "formations" && <FormationsAdmin items={formations} reload={() => listFormations().then(setFormations)} />}
        {tab === "events" && <EventsAdmin items={events} reload={() => listEvents().then(setEvents)} />}
        {tab === "leads" && <LeadsAdmin items={leads} />}
      </div>
    </div>
  );
}

/* ---------------- Properties admin ---------------- */
const EMPTY_FILTERS = { q: "", type: "all", status: "all", pub: "all" };
function PropertiesAdmin({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const [cats, setCats] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [importing, setImporting] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const q = norm(filters.q).trim();
  const filtered = items.filter((p) => {
    if (filters.type !== "all" && p.type !== filters.type) return false;
    if (filters.status !== "all" && p.status !== filters.status) return false;
    if (filters.pub === "yes" && p.published === false) return false;
    if (filters.pub === "no" && p.published !== false) return false;
    if (filters.pub === "review" && !(p.needs_review || []).length) return false;
    if (!q) return true;
    return [p.title, p.location, p.sub_location, p.address, p.postal_code, p.category, p.source_id].some((v) => norm(v).includes(q));
  });
  useEffect(() => { listPropertyCategories().then(setCats).catch(() => setCats([])); }, [items]);
  useEffect(() => { catalogStatus().then(setCatalog).catch(() => setCatalog(null)); }, [items]);
  const runImport = async () => {
    setImporting(true);
    try {
      const r = await catalogImport();
      await reload();
      toast({ title: "Catalogue importé", description: `${r.created} propriétés ajoutées, ${r.skipped} déjà présentes.` });
    } catch {
      toast({ title: "Erreur", description: "L'import du catalogue a échoué." });
    }
    setImporting(false);
  };
  const blank = { title: "", location: "", sub_location: "", address: "", postal_code: "", type: "revente", category: "", status: "disponible", listing_type: "achat", tags: [], price: "", price_from: false, currency: "USD", hoa: "", delivery_date: "", delivery_label: "", bedrooms: "", bedrooms_label: "", bathrooms: "", area: "", area_label: "", lot_area: "", turnkey: null, roi: "", units: "", beach_distance: "", lat: null, lng: null, image: "", gallery: [], video_url: "", videos: [], virtual_tour_url: "", pdf_url: "", description: "", features: [], amenities_available: [], amenities_unavailable: [], published: true };

  const changeStatus = async (id, status) => {
    await updatePropertyStatus(id, status);
    await reload();
    toast({ title: "Statut mis à jour", description: statusMeta(status).label });
  };
  const remove = async (id) => { await deleteProperty(id); await reload(); toast({ title: "Propriété supprimée" }); };
  const submit = async (data) => {
    if (data.id) await updateProperty(data.id, data);
    else await createProperty(data);
    await reload();
    setEditing(null);
    toast({ title: data.id ? "Propriété modifiée" : "Propriété ajoutée" });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-serif-display text-2xl text-[#0b1f33]">Gestion des propriétés</h2>
        <button onClick={() => setEditing(blank)} className="btn-gold flex items-center gap-2 px-5 py-2.5 rounded-md font-600 text-sm"><Plus size={16} /> Ajouter</button>
      </div>
      {catalog && catalog.imported < catalog.catalog && (
        <div data-testid="catalog-import-banner" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#2f74c9]/30 bg-[#2f74c9]/5 px-5 py-4">
          <div>
            <p className="font-600 text-[#0b1f33] text-sm">Catalogue Bella Edifices — {catalog.catalog - catalog.imported} propriétés disponibles à l'import</p>
            <p className="text-xs text-slate-500 mt-0.5">{catalog.catalog} fiches (reventes et préventes) avec photos. Les propriétés déjà présentes ne sont pas modifiées.</p>
          </div>
          <button onClick={runImport} disabled={importing} data-testid="catalog-import-btn" className="inline-flex items-center gap-2 bg-[#1156A7] hover:bg-[#0e478a] text-white text-sm font-600 px-5 py-2.5 rounded-md transition-colors disabled:opacity-60">
            <Upload size={15} /> {importing ? "Import en cours…" : "Importer le catalogue"}
          </button>
        </div>
      )}
      <div className="bg-white rounded-lg border border-slate-100 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="property-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="property-search-input" placeholder="Rechercher : titre, lieu, adresse, catégorie…" className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:border-[#2f74c9]" />
        </div>
        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="property-filter-type" className="rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#2f74c9] bg-white">
          <option value="all">Tous les types</option>
          {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="property-filter-status" className="rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#2f74c9] bg-white">
          <option value="all">Tous les statuts</option>
          {PROPERTY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filters.pub} onChange={(e) => setFilters({ ...filters, pub: e.target.value })} data-testid="property-filter-published" className="rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#2f74c9] bg-white">
          <option value="all">Publiées et non publiées</option>
          <option value="yes">Publiées</option>
          <option value="no">Non publiées</option>
          <option value="review">À vérifier</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto" data-testid="property-filter-count">{filtered.length} / {items.length}</span>
        {(filters.q || filters.type !== "all" || filters.status !== "all" || filters.pub !== "all") && (
          <button onClick={() => setFilters(EMPTY_FILTERS)} data-testid="property-filter-reset" className="text-xs text-[#2f74c9] hover:underline">Réinitialiser</button>
        )}
      </div>
      <div className="bg-white rounded-lg border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-5 py-3 font-500">Propriété</th>
              <th className="px-5 py-3 font-500 hidden md:table-cell">Type</th>
              <th className="px-5 py-3 font-500 hidden md:table-cell">Prix</th>
              <th className="px-5 py-3 font-500">Statut</th>
              <th className="px-5 py-3 font-500 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400" data-testid="property-filter-empty">Aucune propriété ne correspond à votre recherche.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <img src={mediaUrl(p.image)} alt="" loading="lazy" decoding="async" className="w-12 h-12 rounded-md object-cover bg-slate-100" />
                    <div>
                      <p className="font-600 text-[#0b1f33] flex items-center gap-2">
                        {p.title}
                        {p.published === false && <span className="text-[10px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Non publié</span>}
                        {(p.needs_review || []).length > 0 && <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full" title={p.needs_review.join(', ')}>À vérifier</span>}
                      </p>
                      <p className="text-xs text-slate-400">{p.location}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 hidden md:table-cell capitalize">{p.type}</td>
                <td className="px-5 py-3 hidden md:table-cell">{formatPrice(p.price, p.currency)}</td>
                <td className="px-5 py-3">
                  <select value={p.status} onChange={(e) => changeStatus(p.id, e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-[#2f74c9]">
                    {PROPERTY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setEditing(p)} className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><Edit3 size={16} /></button>
                    <button onClick={() => remove(p.id)} className="p-2 rounded-md hover:bg-rose-50 text-rose-500"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PropertyForm data={editing} onCancel={() => setEditing(null)} onSubmit={submit} categories={cats} />}
    </div>
  );
}

function PropertyForm({ data, onCancel, onSubmit, categories = [] }) {
  const [f, setF] = useState({
    ...data,
    featuresText: (data.features || []).join("\n"),
    amenAvailText: (data.amenities_available || []).join("\n"),
    amenUnavailText: (data.amenities_unavailable || []).join("\n"),
    videosText: (data.videos || []).join("\n"),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const intNum = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));
  const lines = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const catOptions = Array.from(new Set([...(categories || []), "Studio", "1 chambre", "2 chambres", "3 chambres", "4+ chambres", "Hôtel", "Beachfront", "Terrain", "Bâtisse commerciale"]));
  const save = async () => {
    setSaving(true);
    const payload = {
      ...f,
      price: num(f.price),
      area: num(f.area),
      hoa: num(f.hoa),
      lot_area: num(f.lot_area),
      bedrooms: intNum(f.bedrooms),
      bathrooms: num(f.bathrooms),
      lat: num(f.lat),
      lng: num(f.lng),
      gallery: f.gallery || [],
      tags: typeof f.tags === "string" ? f.tags.split(",").map((s) => s.trim()).filter(Boolean) : (f.tags || []),
      features: lines(f.featuresText),
      amenities_available: lines(f.amenAvailText),
      amenities_unavailable: lines(f.amenUnavailText),
      videos: lines(f.videosText),
    };
    ["featuresText", "amenAvailText", "amenUnavailText", "videosText"].forEach((k) => delete payload[k]);
    try { await onSubmit(payload); }
    catch { toast({ title: "Erreur", description: "Enregistrement impossible. Vérifiez les champs." }); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={f.id ? "Modifier la propriété" : "Nouvelle propriété"} onCancel={onCancel}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Titre"><input value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} /></Field>
        <Field label="Localisation (ville)"><input value={f.location} onChange={(e) => set("location", e.target.value)} className={inp} /></Field>
        <Field label="Adresse"><input value={f.address || ""} onChange={(e) => set("address", e.target.value)} className={inp} /></Field>
        <Field label="Code postal"><input value={f.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} className={inp} /></Field>
        <Field label="Type">
          <select value={f.type} onChange={(e) => set("type", e.target.value)} className={inp}>
            {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Catégorie"><input list="cat-list" value={f.category || ""} onChange={(e) => set("category", e.target.value)} className={inp} placeholder="Studio, 1 chambre, Beachfront, Terrain…" />
          <datalist id="cat-list">
            {catOptions.map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>
        <Field label="Offre">
          <select value={f.listing_type || "achat"} onChange={(e) => set("listing_type", e.target.value)} className={inp}>
            <option value="achat">À acheter</option>
            <option value="location">À louer</option>
          </select>
        </Field>
        <Field label="Statut">
          <select value={f.status} onChange={(e) => set("status", e.target.value)} className={inp}>
            {PROPERTY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Sous-secteur (Zazil-Ha, Centro, Playacar…)"><input value={f.sub_location || ""} onChange={(e) => set("sub_location", e.target.value)} className={inp} /></Field>
        <Field label="Prix"><input type="number" value={f.price ?? ""} onChange={(e) => set("price", e.target.value)} className={inp} placeholder="(vide = Prix sur demande)" /></Field>
        <Field label="Devise"><input value={f.currency ?? ""} onChange={(e) => set("currency", e.target.value)} className={inp} placeholder="USD" /></Field>
        <Field label="« À partir de » (projets)">
          <label className="flex items-center gap-2 mt-2 text-sm text-slate-600"><input type="checkbox" checked={!!f.price_from} onChange={(e) => set("price_from", e.target.checked)} /> Afficher « À partir de »</label>
        </Field>
        <Field label="Frais de condo / HOA (nombre, sans devise)"><input type="number" value={f.hoa ?? ""} onChange={(e) => set("hoa", e.target.value)} className={inp} placeholder="350" /></Field>
        <Field label="Surface (m²)"><input type="number" value={f.area ?? ""} onChange={(e) => set("area", e.target.value)} className={inp} /></Field>
        <Field label="Surface (texte, projets)"><input value={f.area_label || ""} onChange={(e) => set("area_label", e.target.value)} className={inp} placeholder="32 à 81 m²" /></Field>
        <Field label="Terrain (m²)"><input type="number" value={f.lot_area ?? ""} onChange={(e) => set("lot_area", e.target.value)} className={inp} /></Field>
        <Field label="Chambres"><input type="number" value={f.bedrooms ?? ""} onChange={(e) => set("bedrooms", e.target.value)} className={inp} /></Field>
        <Field label="Chambres (texte, projets)"><input value={f.bedrooms_label || ""} onChange={(e) => set("bedrooms_label", e.target.value)} className={inp} placeholder="Studio à 2 chambres" /></Field>
        <Field label="Salles de bain (2,5 possible)"><input type="number" step="0.5" value={f.bathrooms ?? ""} onChange={(e) => set("bathrooms", e.target.value)} className={inp} /></Field>
        <Field label="Livraison (date)"><input type="date" value={f.delivery_date || ""} onChange={(e) => set("delivery_date", e.target.value)} className={inp} /></Field>
        <Field label="Livraison (texte)"><input value={f.delivery_label || ""} onChange={(e) => set("delivery_label", e.target.value)} className={inp} placeholder="Nov. 2027 / Prête" /></Field>
        <Field label="Rendement (ROI)"><input value={f.roi || ""} onChange={(e) => set("roi", e.target.value)} className={inp} /></Field>
        <Field label="Unités"><input value={f.units || ""} onChange={(e) => set("units", e.target.value)} className={inp} /></Field>
        <Field label="Distance plage"><input value={f.beach_distance || ""} onChange={(e) => set("beach_distance", e.target.value)} className={inp} /></Field>
        <Field label="Clé en main">
          <label className="flex items-center gap-2 mt-2 text-sm text-slate-600"><input type="checkbox" checked={!!f.turnkey} onChange={(e) => set("turnkey", e.target.checked)} /> Clé en main</label>
        </Field>
        <Field label="Tags / pastilles (séparés par des virgules)" full>
          <input value={Array.isArray(f.tags) ? f.tags.join(", ") : (f.tags || "")} onChange={(e) => set("tags", e.target.value)} className={inp} placeholder="Bord de mer, Vue mer, Neuf…" />
        </Field>
        <Field label="Latitude (carte)"><input type="number" step="0.0001" value={f.lat ?? ""} onChange={(e) => set("lat", e.target.value === "" ? null : Number(e.target.value))} className={inp} placeholder="20.6296" /></Field>
        <Field label="Longitude (carte)"><input type="number" step="0.0001" value={f.lng ?? ""} onChange={(e) => set("lng", e.target.value === "" ? null : Number(e.target.value))} className={inp} placeholder="-87.0739" /></Field>
        <Field label="Lien vidéo (visite commentée)"><input value={f.video_url || ""} onChange={(e) => set("video_url", e.target.value)} className={inp} placeholder="https://youtube.com/…" /></Field>
        <Field label="Lien visite virtuelle"><input value={f.virtual_tour_url || ""} onChange={(e) => set("virtual_tour_url", e.target.value)} className={inp} placeholder="https://my.matterport.com/…" /></Field>
        <Field label="Vidéos YouTube (une URL par ligne)" full>
          <textarea rows={2} value={f.videosText} onChange={(e) => set("videosText", e.target.value)} className={inp} />
        </Field>
        <Field label="Fiche PDF (URL)" full><input value={f.pdf_url || ""} onChange={(e) => set("pdf_url", e.target.value)} className={inp} /></Field>
        <div className="md:col-span-2"><MultiImageField value={f.gallery} onChange={(v) => set("gallery", v)} /></div>
        <Field label="Prestations / Atouts (une par ligne)" full>
          <textarea rows={3} value={f.featuresText} onChange={(e) => set("featuresText", e.target.value)} className={inp} placeholder={"Piscine\nSalle de sport\nSécurité 24/7"} />
        </Field>
        <Field label="Équipements inclus (une par ligne)" full>
          <textarea rows={2} value={f.amenAvailText} onChange={(e) => set("amenAvailText", e.target.value)} className={inp} />
        </Field>
        <Field label="Équipements non inclus (une par ligne)" full>
          <textarea rows={2} value={f.amenUnavailText} onChange={(e) => set("amenUnavailText", e.target.value)} className={inp} />
        </Field>
        <Field label="Description" full><textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} /></Field>
        <div className="md:col-span-2 flex items-center justify-between border-t border-slate-100 pt-4">
          <label className="flex items-center gap-3 text-sm font-600 text-[#0b1f33]">
            <input type="checkbox" checked={f.published !== false} onChange={(e) => set("published", e.target.checked)} className="w-5 h-5 accent-[#1156a7]" />
            Publié (visible sur le site)
          </label>
          {(f.needs_review || []).length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-full">À vérifier : {f.needs_review.join(", ")}</span>
          )}
        </div>
      </div>
      <FormActions onCancel={onCancel} onSubmit={save} saving={saving} />
    </Modal>
  );
}

/* ---------------- Formations admin ---------------- */
function FormationsAdmin({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const blank = { title: "", level: "Débutant", duration: "4 semaines", price: 0, format: "En ligne", lessons: 10, image: "", description: "", cta_label: "S'inscrire", cta_link: "" };
  const remove = async (id) => { await deleteFormation(id); await reload(); toast({ title: "Formation supprimée" }); };
  const submit = async (d) => { d.id ? await updateFormation(d.id, d) : await createFormation(d); await reload(); setEditing(null); toast({ title: d.id ? "Formation modifiée" : "Formation ajoutée" }); };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-serif-display text-2xl text-[#0b1f33]">Gestion des formations</h2>
        <button onClick={() => setEditing(blank)} className="btn-gold flex items-center gap-2 px-5 py-2.5 rounded-md font-600 text-sm"><Plus size={16} /> Ajouter</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((f) => (
          <div key={f.id} className="bg-white rounded-lg border border-slate-100 overflow-hidden">
            <img src={mediaUrl(f.image)} alt="" className="w-full h-36 object-cover bg-slate-100" />
            <div className="p-5">
              <p className="font-600 text-[#0b1f33]">{f.title}</p>
              <p className="text-xs text-slate-400 mt-1">{f.level} · {f.duration} · {formatPrice(f.price)}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditing(f)} className="flex-1 flex items-center justify-center gap-1.5 text-sm border border-slate-200 rounded-md py-2 hover:border-[#2f74c9]"><Edit3 size={14} /> Modifier</button>
                <button onClick={() => remove(f.id)} className="p-2 rounded-md hover:bg-rose-50 text-rose-500"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Modifier la formation" : "Nouvelle formation"} onCancel={() => setEditing(null)}>
          <FormationFields data={editing} onCancel={() => setEditing(null)} onSubmit={submit} />
        </Modal>
      )}
    </div>
  );
}

function FormationFields({ data, onCancel, onSubmit }) {
  const [f, setF] = useState(data);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setSaving(true); try { await onSubmit(f); } catch { toast({ title: "Erreur", description: "Enregistrement impossible." }); } finally { setSaving(false); } };
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Titre" full><input value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} /></Field>
        <Field label="Niveau"><input value={f.level} onChange={(e) => set("level", e.target.value)} className={inp} /></Field>
        <Field label="Durée"><input value={f.duration} onChange={(e) => set("duration", e.target.value)} className={inp} /></Field>
        <Field label="Format"><input value={f.format} onChange={(e) => set("format", e.target.value)} className={inp} /></Field>
        <Field label="Prix (USD)"><input type="number" value={f.price} onChange={(e) => set("price", Number(e.target.value))} className={inp} /></Field>
        <Field label="Leçons"><input type="number" value={f.lessons} onChange={(e) => set("lessons", Number(e.target.value))} className={inp} /></Field>
        <Field label="Libellé du bouton"><input value={f.cta_label ?? "S'inscrire"} onChange={(e) => set("cta_label", e.target.value)} className={inp} placeholder="S'inscrire" /></Field>
        <Field label="Lien du bouton (Skool, etc.)"><input value={f.cta_link || ""} onChange={(e) => set("cta_link", e.target.value)} className={inp} placeholder="https://skool.com/…" /></Field>
        <div className="md:col-span-2"><ImageField value={f.image} onChange={(v) => set("image", v)} /></div>
        <Field label="Description" full><textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} /></Field>
      </div>
      <FormActions onCancel={onCancel} onSubmit={save} saving={saving} />
    </>
  );
}

/* ---------------- Events admin ---------------- */
function EventsAdmin({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const blank = { title: "", date: "2025-12-01", time: "18:00", location: "", type: "Présentiel", seats: 30, image: "", description: "", cta_label: "Réserver ma place", cta_link: "" };
  const remove = async (id) => { await deleteEvent(id); await reload(); toast({ title: "Événement supprimé" }); };
  const submit = async (d) => { d.id ? await updateEvent(d.id, d) : await createEvent(d); await reload(); setEditing(null); toast({ title: d.id ? "Événement modifié" : "Événement ajouté" }); };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-serif-display text-2xl text-[#0b1f33]">Gestion des événements</h2>
        <button onClick={() => setEditing(blank)} className="btn-gold flex items-center gap-2 px-5 py-2.5 rounded-md font-600 text-sm"><Plus size={16} /> Ajouter</button>
      </div>
      <div className="space-y-4">
        {items.map((e) => (
          <div key={e.id} className="bg-white rounded-lg border border-slate-100 p-4 flex items-center gap-4">
            <img src={mediaUrl(e.image)} alt="" loading="lazy" className="w-20 h-20 rounded-md object-cover bg-slate-100" />
            <div className="flex-1">
              <p className="font-600 text-[#0b1f33]">{e.title}</p>
              <p className="text-xs text-slate-400 mt-1">{e.date} · {e.time} · {e.location} · {e.type}</p>
            </div>
            <button onClick={() => setEditing(e)} className="p-2 rounded-md hover:bg-slate-100 text-slate-500"><Edit3 size={16} /></button>
            <button onClick={() => remove(e.id)} className="p-2 rounded-md hover:bg-rose-50 text-rose-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Modifier l'événement" : "Nouvel événement"} onCancel={() => setEditing(null)}>
          <EventFields data={editing} onCancel={() => setEditing(null)} onSubmit={submit} />
        </Modal>
      )}
    </div>
  );
}

function EventFields({ data, onCancel, onSubmit }) {
  const [f, setF] = useState(data);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setSaving(true); try { await onSubmit(f); } catch { toast({ title: "Erreur", description: "Enregistrement impossible." }); } finally { setSaving(false); } };
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Titre" full><input value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} /></Field>
        <Field label="Date"><input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={inp} /></Field>
        <Field label="Heure"><input value={f.time} onChange={(e) => set("time", e.target.value)} className={inp} /></Field>
        <Field label="Lieu"><input value={f.location} onChange={(e) => set("location", e.target.value)} className={inp} /></Field>
        <Field label="Type"><input value={f.type} onChange={(e) => set("type", e.target.value)} className={inp} /></Field>
        <Field label="Places"><input type="number" value={f.seats} onChange={(e) => set("seats", Number(e.target.value))} className={inp} /></Field>
        <Field label="Libellé du bouton"><input value={f.cta_label ?? "Réserver ma place"} onChange={(e) => set("cta_label", e.target.value)} className={inp} placeholder="Réserver ma place" /></Field>
        <Field label="Lien du bouton (Eventbrite, etc.)"><input value={f.cta_link || ""} onChange={(e) => set("cta_link", e.target.value)} className={inp} placeholder="https://eventbrite.com/…" /></Field>
        <div className="md:col-span-2"><ImageField value={f.image} onChange={(v) => set("image", v)} /></div>
        <Field label="Description" full><textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} /></Field>
      </div>
      <FormActions onCancel={onCancel} onSubmit={save} saving={saving} />
    </>
  );
}

/* ---------------- Guesty rentals admin (visibility) ---------------- */
function RentalsAdmin({ data, reload }) {
  const [busy, setBusy] = useState(null);
  const toggle = async (l) => {
    setBusy(l.id);
    try {
      await setRentalVisibility(l.id, !l.hidden);
      await reload();
      toast({ title: l.hidden ? "Location affichée sur le site" : "Location masquée du site" });
    } catch {
      toast({ title: "Erreur", description: "Impossible de modifier la visibilité." });
    }
    setBusy(null);
  };
  const visible = data.items.filter((l) => !l.hidden).length;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-serif-display text-2xl text-[#0b1f33]">Locations Guesty</h2>
        <span className="text-sm text-slate-500" data-testid="rentals-visible-count">{visible} affichée{visible > 1 ? "s" : ""} sur {data.items.length}</span>
      </div>
      {!data.enabled ? (
        <p className="text-slate-400 py-10 text-center bg-white rounded-lg border border-slate-100" data-testid="rentals-disabled-msg">Guesty n'est pas connecté. Ajoutez les identifiants Guesty pour activer les locations en direct.</p>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-5">Les locations sont synchronisées depuis Guesty. Masquez celles que vous ne souhaitez pas afficher sur le site — Guesty n'est pas modifié.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.items.map((l) => (
              <div key={l.id} data-testid={`admin-rental-${l.id}`} className={`bg-white rounded-lg border overflow-hidden ${l.hidden ? "border-slate-200 opacity-70" : "border-slate-100"}`}>
                <div className="relative h-36 bg-slate-100">
                  {l.photos[0] && <img src={l.photos[0]} alt="" loading="lazy" className={`w-full h-full object-cover ${l.hidden ? "grayscale" : ""}`} />}
                  <span className={`absolute top-3 left-3 text-[11px] font-600 px-2.5 py-1 rounded-full ${l.hidden ? "bg-slate-700 text-white" : "bg-emerald-500 text-white"}`}>{l.hidden ? "Masquée" : "Affichée"}</span>
                </div>
                <div className="p-4">
                  <p className="font-600 text-[#0b1f33] text-sm leading-snug line-clamp-2">{l.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{l.city}{l.nightly_price != null ? ` · ${new Intl.NumberFormat("fr-FR").format(l.nightly_price)} ${l.currency || ""} / nuit` : ""}</p>
                  <button onClick={() => toggle(l)} disabled={busy === l.id} data-testid={`rental-toggle-${l.id}`} className={`mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-600 px-4 py-2 rounded-md transition-colors disabled:opacity-60 ${l.hidden ? "bg-[#1156A7] text-white hover:bg-[#0e478a]" : "border border-slate-200 text-slate-700 hover:border-[#2f74c9]"}`}>
                    {l.hidden ? <><Eye size={15} /> Afficher sur le site</> : <><EyeOff size={15} /> Masquer du site</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Leads admin ---------------- */
function replyHref(l) {
  const subject = `Re : ${l.subject || "Votre message"} — Bella Edifices`;
  const first = (l.name || "").trim().split(/\s+/)[0] || "";
  const body = `Bonjour ${first},\n\nMerci pour votre message concernant « ${l.subject || "votre projet"} ».\n\n\n\nCordialement,\nL'équipe Bella Edifices\ninfo@bellaedifices.com\n\n---\nVotre message :\n${l.message || ""}`;
  return `mailto:${encodeURIComponent(l.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function LeadsAdmin({ items }) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const runTest = async () => {
    setTesting(true);
    try { setTestResult(await sendEmailTest()); } catch { setTestResult({ ok: false, error: "Requête échouée" }); }
    setTesting(false);
  };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-serif-display text-2xl text-[#0b1f33]">Messages reçus</h2>
        <button onClick={runTest} disabled={testing} data-testid="email-test-btn" className="inline-flex items-center gap-2 text-sm border border-slate-200 hover:border-[#2f74c9] text-[#0b1f33] px-4 py-2 rounded-md transition-colors disabled:opacity-60">
          <MailCheck size={15} className="text-[#2f74c9]" /> {testing ? "Envoi…" : "Tester l'envoi d'email"}
        </button>
      </div>
      {testResult && (
        <div data-testid="email-test-result" className={`mb-5 rounded-md border px-4 py-3 text-sm ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
          {testResult.ok ? `Email de test envoyé à ${testResult.to} depuis ${testResult.from}.` : `Échec de l'envoi (${testResult.from} → ${testResult.to}) : ${testResult.error}`}
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-slate-400 py-10 text-center bg-white rounded-lg border border-slate-100">Aucun message pour le moment.</p>
      ) : (
        <div className="space-y-3">
          {items.map((l) => (
            <div key={l.id} className="bg-white rounded-lg border border-slate-100 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-600 text-[#0b1f33]">{l.name} <span className="text-xs font-400 text-[#2f74c9] ml-2">{l.subject}</span></p>
                <span className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>
              <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                <span data-testid={`lead-forwarded-${l.id}`} className={`px-2 py-0.5 rounded-full ${l.forwarded ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{l.forwarded ? "Transféré à l'équipe" : "Non transféré"}</span>
                <span data-testid={`lead-ack-${l.id}`} className={`px-2 py-0.5 rounded-full ${l.confirmation_sent ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{l.confirmation_sent ? "Accusé envoyé au visiteur" : "Accusé non envoyé"}</span>
              </div>
              <p className="text-sm text-slate-700 mt-3 leading-relaxed whitespace-pre-line">{l.message}</p>
              <div className="mt-4 flex justify-end">
                <a href={replyHref(l)} data-testid={`lead-reply-btn-${l.id}`} className="inline-flex items-center gap-2 bg-[#1156A7] hover:bg-[#0e478a] text-white text-sm font-600 px-4 py-2 rounded-md transition-colors">
                  <Reply size={15} /> Répondre
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Services admin (rentals & management) ---------------- */
function ServicesAdmin({ items, reload }) {
  const [editing, setEditing] = useState(null);
  const blank = { category: "short_term", title: "", description: "", image: "", features: [], cta_label: "En savoir plus", cta_link: "/contact", order: 1 };
  const remove = async (id) => { await deleteService(id); await reload(); toast({ title: "Service supprimé" }); };
  const submit = async (d) => { d.id ? await updateService(d.id, d) : await createService(d); await reload(); setEditing(null); toast({ title: d.id ? "Service modifié" : "Service ajouté" }); };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-serif-display text-2xl text-[#0b1f33]">Locations & gestion</h2>
        <button onClick={() => setEditing(blank)} className="btn-gold flex items-center gap-2 px-5 py-2.5 rounded-md font-600 text-sm"><Plus size={16} /> Ajouter</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((s) => (
          <div key={s.id} className="bg-white rounded-lg border border-slate-100 overflow-hidden">
            <img src={mediaUrl(s.image)} alt="" className="w-full h-36 object-cover bg-slate-100" />
            <div className="p-5">
              <span className="text-[11px] uppercase tracking-wider text-[#2f74c9] font-600">{catLabel(s.category)}</span>
              <p className="font-600 text-[#0b1f33] mt-1">{s.title}</p>
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{s.description}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditing(s)} className="flex-1 flex items-center justify-center gap-1.5 text-sm border border-slate-200 rounded-md py-2 hover:border-[#2f74c9]"><Edit3 size={14} /> Modifier</button>
                <button onClick={() => remove(s.id)} className="p-2 rounded-md hover:bg-rose-50 text-rose-500"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing.id ? "Modifier le service" : "Nouveau service"} onCancel={() => setEditing(null)}>
          <ServiceFields data={editing} onCancel={() => setEditing(null)} onSubmit={submit} />
        </Modal>
      )}
    </div>
  );
}

function ServiceFields({ data, onCancel, onSubmit }) {
  const [f, setF] = useState({ ...data, featuresText: (data.features || []).join("\n") });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setSaving(true);
    const payload = {
      category: f.category, title: f.title, description: f.description, image: f.image,
      cta_label: f.cta_label, cta_link: f.cta_link, order: Number(f.order) || 0,
      features: f.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    if (f.id) payload.id = f.id;
    try { await onSubmit(payload); } catch { toast({ title: "Erreur", description: "Enregistrement impossible." }); } finally { setSaving(false); }
  };
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Catégorie">
          <select value={f.category} onChange={(e) => set("category", e.target.value)} className={inp}>
            {SERVICE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Ordre d'affichage"><input type="number" value={f.order} onChange={(e) => set("order", e.target.value)} className={inp} /></Field>
        <Field label="Titre" full><input value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} /></Field>
        <Field label="Description" full><textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} /></Field>
        <div className="md:col-span-2"><ImageField value={f.image} onChange={(v) => set("image", v)} /></div>
        <Field label="Atouts (une ligne par atout)" full>
          <textarea rows={5} value={f.featuresText} onChange={(e) => set("featuresText", e.target.value)} className={inp} placeholder={"Diffusion multi-plateformes\nTarification dynamique\n..."} />
        </Field>
        <Field label="Libellé du bouton"><input value={f.cta_label} onChange={(e) => set("cta_label", e.target.value)} className={inp} /></Field>
        <Field label="Lien du bouton"><input value={f.cta_link} onChange={(e) => set("cta_link", e.target.value)} className={inp} /></Field>
      </div>
      <FormActions onCancel={onCancel} onSubmit={save} saving={saving} />
    </>
  );
}

/* ---------------- shared bits ---------------- */
const inp = "w-full rounded-md border border-slate-200 px-3 py-2.5 outline-none focus:border-[#2f74c9] focus:ring-2 focus:ring-[#2f74c9]/20 transition text-sm";

function ImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      onChange(url);
      toast({ title: "Image téléversée" });
    } catch {
      toast({ title: "Échec du téléversement" });
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1.5">Image</label>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={22} className="text-slate-300" />}
        </div>
        <div className="flex-1 space-y-2">
          <label className="inline-flex items-center gap-2 text-sm font-600 text-[#2f74c9] border border-[#2f74c9]/40 rounded-md px-4 py-2 cursor-pointer hover:bg-[#2f74c9]/5">
            <Upload size={15} /> {uploading ? "Téléversement…" : "Téléverser une photo"}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
          </label>
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="ou coller une URL d'image" className={inp} />
        </div>
      </div>
    </div>
  );
}
function MultiImageField({ value = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const list = value || [];
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const { url } = await uploadImage(file);
        urls.push(url);
      }
      onChange([...list, ...urls]);
      toast({ title: `${urls.length} image(s) téléversée(s)` });
    } catch {
      toast({ title: "Échec du téléversement" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const arr = [...list];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const remove = (i) => onChange(list.filter((_, k) => k !== i));
  const addUrl = () => {
    const u = window.prompt("Coller l'URL de l'image");
    if (u) onChange([...list, u.trim()]);
  };
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1.5">Photos (la première est l'image principale — utilisez les flèches pour réordonner)</label>
      <div className="flex flex-wrap gap-3">
        {list.map((g, i) => (
          <div key={g} className="relative w-28 group">
            <div className="w-28 h-20 rounded-md overflow-hidden border border-slate-200 bg-slate-50">
              <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" />
            </div>
            {i === 0 && <span className="absolute top-1 left-1 bg-[#1156a7] text-white text-[9px] font-700 px-1.5 py-0.5 rounded">PRINCIPALE</span>}
            <div className="flex items-center justify-between mt-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"><ChevronLeft size={14} /></button>
              <button type="button" onClick={() => remove(i)} className="p-1 rounded hover:bg-rose-50 text-rose-500"><Trash2 size={13} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"><ChevronRight size={14} /></button>
            </div>
          </div>
        ))}
        <label className="w-28 h-20 rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1156a7] text-slate-400 hover:text-[#1156a7]">
          <Upload size={18} />
          <span className="text-[10px] mt-1">{uploading ? "…" : "Ajouter"}</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading} />
        </label>
      </div>
      <button type="button" onClick={addUrl} className="text-xs text-[#1156a7] mt-2 underline">ou ajouter par URL</button>
    </div>
  );
}



const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-xs text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);
const FormActions = ({ onCancel, onSubmit, saving }) => (
  <div className="flex justify-end gap-3 mt-6">
    <button onClick={onCancel} className="px-5 py-2.5 rounded-md text-sm border border-slate-200 hover:bg-slate-50">Annuler</button>
    <button onClick={onSubmit} disabled={saving} className="btn-gold px-5 py-2.5 rounded-md text-sm font-600 flex items-center gap-2 disabled:opacity-60"><Check size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}</button>
  </div>
);
const Modal = ({ title, children, onCancel }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto overscroll-contain" data-testid="admin-modal">
    <div className="bg-white rounded-lg w-full max-w-2xl my-4 sm:my-8 p-7">
      <div className="flex items-center justify-between mb-6 sticky -top-7 -mx-7 px-7 py-4 bg-white/95 backdrop-blur z-10 border-b border-slate-100 rounded-t-lg">
        <h3 className="font-serif-display text-2xl text-[#0b1f33]">{title}</h3>
        <button onClick={onCancel} data-testid="admin-modal-close" className="p-2 rounded-md hover:bg-slate-100"><X size={20} /></button>
      </div>
      {children}
    </div>
  </div>
);
